import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Routes,
  Filters,
  GaAnalyticEvents,
  SessionStorageKeys,
  PubSubEvents,
} from '../../constants';
import {
  getUserLocation,
  getUserAddressByCoords,
  getUserLocationByAddress,
  getBaiduUserLocationByAddress,
  getBaiduUserAddressByCoords,
} from '../../utils/geocodeUtils';
import {
  generateUrlPayload,
  parseQueryStrings,
  stringifyQueryStrings,
} from '../../utils/urlParamUtils';
import withRouter from '../../hoc/withRouter';
import withConfig from '../../hoc/withConfig';
import { trackEvent } from '../../utils/analyticUtils';
import { generateRoute } from '../../utils/routeUtils';
import GeocoderService from '../../services/geocoderService';
import SearchAutocomplete from '../searchAutocomplete/searchAutocomplete';
import { getTenantUnitOfMeasure } from '../../utils/tenantUtils';
import { getDefaultSearchDistanceRadius } from '../../utils/searchFilterUtils';
import { replaceHashWithUrlCode } from '../../utils/stringUtils';
import Session from '../../session';
import { pluckBaiduCityState, pluckCityState } from '../../utils/searchUtils';
import Events from '../../events';
import bemify from '../../utils/bemUtils';

/**
 *
 */
class Search extends Component {
  constructor(props) {
    super(props);

    this.state = {
      address: '',
      chainNameSearch: null,
    };
    this._geocoderService = new GeocoderService();
    this._addressUrl = null;
  }

  /**
   *
   */
  componentDidMount() {
    const { search, hash } = this.props.location;
    const { address } = parseQueryStrings(search, hash);
    const { practiceIdentifier, address: locatorTenancyAddress } =
      window.locatorTenancy;

    this._addressUrl = address;

    if (practiceIdentifier) {
      this.redirectToPracticeDetails(practiceIdentifier);
    }

    if (locatorTenancyAddress) {
      this.setState(() => ({ address: locatorTenancyAddress }));
      this.onSelectSuggestion(locatorTenancyAddress);
    }

    if (address) {
      this.setState(() => ({ address: address }));
    }

    Events.on(PubSubEvents.appendChainSearch, value =>
      this.onAppendChainName(value)
    );
  }

  /**
   *
   * @param {*} prevProps
   */
  componentDidUpdate(prevProps) {
    const { search, hash } = this.props.location;

    if (search !== prevProps.location.search) {
      const { address } = parseQueryStrings(search, hash);

      this.setState(() => ({ address: address }));
      // this.setState(() => ({ address: 'nice address' }));
      this._addressUrl = address;
    }
  }

  componentWillUnmount() {
    Events.removeListener(PubSubEvents.appendChainSearch, value =>
      this.onAppendChainName(value)
    );
  }

  redirectToPracticeDetails = practiceIdentifier => {
    const { config, navigate } = this.props;
    const detailsPath = `${generateRoute(
      config,
      Routes.details
    )}/${practiceIdentifier}`;
    // navigate() should be used inside useEffect() but it's not possible since we're using class component.
    // componentDidMount() is rendered after useEffect() so setTimeout() is just a workaround to call navigate().
    return setTimeout(() => navigate(`/${detailsPath}`));
  };

  getPostalCode = addressComponents => {
    const postalCodeObj = addressComponents?.find(component =>
      component.types.includes('postal_code')
    );

    if (!postalCodeObj) {
      return '';
    }

    return postalCodeObj.long_name || postalCodeObj.short_name;
  };

  /**
   *
   */
  onClearSearch = () => {
    this.setState(() => ({ address: '' }));
  };

  /**
   *
   */
  onAppendChainName = (value) => {
    this.setState({ chainNameSearch: value });
  };

  /**
   *
   */
  onGetUserLocation = async () => {
    const selectedFilters = Session.getItem(SessionStorageKeys.selectedFilters);
    const isOnlineBookingEnabled = !!selectedFilters?.some(
      (f) => f.key === Filters.onlineBooking
    );

    // TODO: place session based filters here
    const sessionFilters = {};
    if (isOnlineBookingEnabled) {
      sessionFilters.filteredSearch = 1;
      sessionFilters['Online Booking'] = 'Yes';
    }

    try {
      const { config, navigate } = this.props;
      const { use_baidu_map } = config;
      let postalCode = '';
      let shortenedAddress = '';

      const coords = await getUserLocation();

      if (use_baidu_map) {
        const { addressComponent } = await getBaiduUserAddressByCoords(coords);
        shortenedAddress = pluckBaiduCityState(addressComponent);
      } else {
        const { components } = await getUserAddressByCoords(coords);
        shortenedAddress = pluckCityState(components);
        postalCode = this.getPostalCode(components);
      }

      const urlPayload = generateUrlPayload(config, coords, shortenedAddress, this.props.location);

      Session.setItem(SessionStorageKeys.isGeo, true);
      Session.setItem(SessionStorageKeys.allowLocationAccess, true);
      Session.setItem(SessionStorageKeys.userCurrentLocation, {
        lat: coords.latitude,
        long: coords.longitude,
      });

      const queryStrings = stringifyQueryStrings({
        ...urlPayload,
        postalCode: postalCode || '',
        ...sessionFilters,
      });

      const resultsRoute = generateRoute(config, Routes.results);

      trackEvent(GaAnalyticEvents.search.allowLocation);

      navigate(`/${resultsRoute}?${queryStrings}`);
    } catch {
      // User denied allowing us to get their location.
      // We'll use their IP to geo locate them instead.

      const res = await this._geocoderService.geocodeByIp();
      const { city, postal, region, latitude, longitude } = res;

      if (city && region) {
        const { config, isFirstLoad, navigate } = this.props;
        const coords = {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
        };
        const address = `${city}${
          Boolean(region) && region !== 'null' ? `, ${region}` : ''
        }`;
        const urlPayload = generateUrlPayload(config, coords, address, this.props.location);

        urlPayload.isFirstLoad = isFirstLoad;

        Session.setItem(SessionStorageKeys.isGeo, true);
        Session.setItem(SessionStorageKeys.allowLocationAccess, false);
        Session.setItem(SessionStorageKeys.userCurrentLocation, {
          lat: latitude,
          long: longitude,
        });

        const queryStrings = stringifyQueryStrings({
          ...urlPayload,
          postalCode: postal || '',
          ...sessionFilters,
        });

        const resultsRoute = generateRoute(config, Routes.results);

        trackEvent(GaAnalyticEvents.search.declineLocation);

        navigate(`/${resultsRoute}?${queryStrings}`);
      }
    }
  };

  /**
   *
   * @param {string} value
   */
  onSearchChange = value => {
    this.setState(() => ({ address: value }));
  };

  /**
   *
   */
  onGetBaiduLocation = async (suggestion) => {
    const { result } = await getBaiduUserLocationByAddress(suggestion);
    return {
      lat: result.location.lat,
      lng: result.location.lng,
    };
  };

  /**
   *
   */
  onSelectSuggestion = async (suggestion, source, addressLoc) => {
    const { use_baidu_map } = this.props.config;
    const { search, hash } = this.props.location;
    const queryStringValues = parseQueryStrings(search, hash);
    let shortenedAddress = null;

    let latLng = {
      lat: 0,
      lng: 0,
    };

    Session.setItem(SessionStorageKeys.isGeo, false);

    try {
      if (suggestion && suggestion !== queryStringValues.address) {
        if (use_baidu_map) {
          latLng = await this.onGetBaiduLocation(suggestion);
        } 
        else if (source ==='acsByAddress') {
          latLng = {
            lat: addressLoc.addressLat,
            lng: addressLoc.addressLng
          };
        } 
        else {
          const results = await getUserLocationByAddress(suggestion);
          shortenedAddress = pluckCityState(results[0].address_components);
          const { location } = results[0].geometry;
          latLng = {
            lat: location.lat(),
            lng: location.lng(),
          };
        }
      }
    } catch (error) {
      if (error !== 'ZERO_RESULTS') {
        console.error(error);
      }
    } finally {
      const { config, t } = this.props;
      const { Distance, filteredSearch } = queryStringValues;
      const isAcsSuggByChainSearch =
        source === 'acsByChain'
          ? `${replaceHashWithUrlCode(suggestion)} ${t('text_locations')}`
          : replaceHashWithUrlCode(suggestion);

      if (Distance && filteredSearch) {
        queryStringValues.miles = Distance;
      } else {
        queryStringValues.miles = getDefaultSearchDistanceRadius(config);
      }

      if (use_baidu_map) {
        latLng = await this.onGetBaiduLocation(suggestion);
      }

      queryStringValues.address = shortenedAddress ?? isAcsSuggByChainSearch;
      queryStringValues.lat = latLng.lat;
      queryStringValues.long = latLng.lng;
      queryStringValues.currentPage = process.env.LOCATOR_SEARCH_FIRST_PAGE;
      queryStringValues.unitOfMeasurement = getTenantUnitOfMeasure(config);
      queryStringValues.isFirstLoad = this.props.isFirstLoad;
      queryStringValues.chainSearch = this.state.chainNameSearch;

      const selectedFilters = Session.getItem(SessionStorageKeys.selectedFilters);
      const isOnlineBookingEnabled = !!selectedFilters?.some(
        (f) => f.key === Filters.onlineBooking
      );

      if (isOnlineBookingEnabled) {
        queryStringValues.filteredSearch = 1;
        queryStringValues['Online Booking'] = 'Yes';
      }

      const resultsRoute = generateRoute(config, Routes.results);
      const queryStrings = stringifyQueryStrings(queryStringValues);
      const { navigate } = this.props;

      // hide search this area button
      const mapSearchAreaButton = document.getElementsByClassName('map__search-area')[0];
      if (mapSearchAreaButton) {
        mapSearchAreaButton.classList.remove('map__search-area--visible');
      }

      navigate(`/${resultsRoute}?${queryStrings}`);
    }
  };

  /**
   *
   * @returns
   */
  render() {
    const { address } = this.state;
    const { 
      t,
      totalPractices, 
      isFetchingPractice, 
      isHomePage 
    } = this.props;
    const { enable_no_store_results_display, show_distance_in_imperial_units } = this.props.config;
    const { search, hash } = this.props.location;
    const queryStringValues = parseQueryStrings(search, hash);
    const { Distance }= queryStringValues;
    const searchDistanceValue = Distance ? Distance : getDefaultSearchDistanceRadius(this.props.config);
    const tenantUnitOfMeasurement = show_distance_in_imperial_units ? t('text_miles_unit', 'miles') : t('text_kilometer_unit', 'km');
    const [block, element] = bemify('search');

    return (
      <div className={block()}>
        <SearchAutocomplete
          value={address}
          placeholder={t('text_address_placeholder')}
          onChange={this.onSearchChange}
          onClearSearch={this.onClearSearch}
          onGetUserLocation={this.onGetUserLocation}
          onSelectSuggestion={this.onSelectSuggestion}
        />
        {
          (enable_no_store_results_display && !isHomePage && !isFetchingPractice && !(totalPractices > 0)) &&
          <div className={element('no-result-content')}>
            {
              this._addressUrl ? (
                <span>
                  {`${t('text_no_stores_found_within')} ${searchDistanceValue} ${tenantUnitOfMeasurement} ${t('text_of')} `}
                  <span className={element('no-result-content--address')}>
                    {this._addressUrl}
                  </span>
                </span>
              ) : (
                <span>
                  {t('text_no_stores_found_nearby')}
                </span>
              )
            }
          </div>
        }
      </div>
    );
  }
}

Search.propTypes = {
  t: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  isFirstLoad: PropTypes.bool,
  hasPractices: PropTypes.bool,
  totalPractices: PropTypes.number,
  isFetchingPractice: PropTypes.bool,
  isHomePage: PropTypes.bool,
};

export default withConfig(withRouter(withTranslation()(Search)));
